// API Response Types
export interface FindTicketResponse {
  ticketId: string | null;
  ticketNumber: string | null;
  spot: SpotDetails | null;
  vehicleNumber: string | null;
  entryDate: string; // ISO date-time
  exitDate: string | null; // ISO date-time
  status: TicketStatus;
  meta: TicketMeta | null;
}

export interface TicketMeta {
  rate: string | null;
}

export interface SpotDetails {
  code: string | null;
  name: string | null;
}

export enum TicketStatus {
  unknown = "unknown",
  active = "active",    // Can be paid
  paid = "paid",        // Already paid
  voided = "voided"     // Auto-pay on exit
}

// Purchase Request Types
export interface PurchaseOrderRequest {
  offerId: string; // UUID - required
  contact: Contact;
  billingAddress: Address;
  shippingAddress?: Address;
  vehicles?: Vehicle[];
  cards?: CredentialCard[];
  tollTag?: string;
  paymentMethod: PurchaseOrderPaymentMethod;
  services?: ServiceType[];
  validity?: Validity;
  options: PurchaseOrderOptions;
}

export interface Contact {
  firstName: string; // max 50 chars - required
  lastName: string;  // max 50 chars - required
  email: string | null;     // max 50 chars, email format
  phone: string | null;     // max 20 chars, phone format
  gender?: GenderEnum;
  birthday?: string; // ISO date-time
  driversLicense?: string; // max 20 chars
  companyId?: number;
}

export enum GenderEnum {
  male = "male",
  female = "female", 
  other = "other"
}

export interface Address {
  street: string | null;
  city: string | null;
  state: string | null;
  postCode: string; // required
}

export interface Vehicle {
  plate: string | null;
  permit: PermitEnum;
  isDefault: boolean;
}

export enum PermitEnum {
  car = "car",
  motorcycle = "motorcycle",
  truck = "truck",
  bus = "bus",
  taxi = "taxi",
  other = "other"
}

export interface CredentialCard {
  code?: string; // max 9 chars, min 2 chars, numbers only
  isDefault: boolean;
}

export interface PurchaseOrderPaymentMethod {
  newCard: {
    sid?: string; // UUID - "e5c12c6f-ae0c-4a50-865e-235a18944d40"
  };
}

export interface PurchaseOrderOptions {
  openAnAccount: boolean; // true for member, false for guest
  generalTerms: boolean;  // from UI checkbox
  marketingTerms: boolean;  
  notifications: boolean; // set to true
  scanToPay?: {
    ticketToVoid?: string;  // TicketId from ticket details
    ticketName?: string;  // TicketName from ticket details
  };
}

export interface Validity {
  from: string; // ISO date-time
  to: string;   // ISO date-time
}

export enum ServiceType {
  UNKNOWN = "UNKNOWN",
  CAR_WASH = "CAR_WASH",
  EXTENDED_HOURS = "EXTENDED_HOURS",
  SPECIAL_PERKS_AT_NEARBY_BUSINESSES = "SPECIAL_PERKS_AT_NEARBY_BUSINESSES",
  ENHANCED_SECURITY = "ENHANCED_SECURITY",
  OVERSTAY_GUARANTEE = "OVERSTAY_GUARANTEE",
  CONVENIENCE_FEE = "CONVENIENCE_FEE"
}

// Response Types
export interface PurchaseOldOrderResponse {
  order: Order;
  credentials: LoginResponse;
}

export interface OrderSubscriptionOptions {
  isAutoRenewable?: boolean;
}

export interface OrderOptions {
  subscription?: OrderSubscriptionOptions;
  showQR?: boolean;
}

export interface Order {
  id: string; // UUID
  shortId?: string;
  status: OrderStatus;
  total: Price;
  contact: Contact;
  billingAddress: Address;
  options?: OrderOptions;
}

export enum OrderStatus {
  pending = "pending",
  success = "success",
  onerror = "onerror",
  awaitingApproval = "awaitingApproval",
  canceled = "canceled",
  declined = "declined",
  deactivated = "deactivated"
}

/** GET /api/v1/scan2pay/orders/{orderId}/void-status (backend: TicketVoidStatusRequest) */
export type OrderTicketVoidStatus = 'pending' | 'success' | 'error';

export interface OrderTicketVoidStatusResponse {
  status: OrderTicketVoidStatus;
}

export interface Price {
  incVat: number;
  excVat: number;
}

export interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiresIn: number;
  refreshTokenExpiresIn: number;
}

// Error Types
export interface FormValidationResponse {
  message: string | null;
  errors: Record<string, string[]> | null;
}

export interface HttpMessageModel {
  message: string;
}

// HPP (Hosted Payment Page) Types
export type HppStatus = 'pending' | 'success' | 'failed' | 'canceled';

export interface HppSessionStatusResponse {
  id: string; // UUID format
  status: HppStatus;
}

export type TokenizerStrategy = 'zeroSale' | 'authOnly' | 'saleAndRefund';

export interface TokenizationInstructionsRequest {
  provider?: string | null;
  strategy?: TokenizerStrategy;
}

export interface TokenizationInstructionsResponse {
  sid: string; // UUID format
  payloadBase64: string | null;
  provider: string | null;
}

export interface DatacapCallbackPayload {
  token: string | null;
  brand: string | null;
  expirationMonth: string | null;
  expirationYear: string | null;
  last4: string | null;
  bin: string | null;
  cvv: string | null;
  error: string | null;
}

export interface DatacapCallbackRequest {
  sid: string; // UUID format
  payload: DatacapCallbackPayload;
}

// Card Payment Modal types

export interface ExistingCard {
  holder: string;
  type: 'unknown' | 'visa' | 'masterCard' | 'maestro' | 'amex' | 'diners' | 'jcb' | 'discover';
  last4D: string;
  expiry: string; // MM/YY
  token: string;
  isDefault: boolean;
}

export type CardPaymentModalResult = {
  status: 'success' | 'cancelled' | 'failed';
  selected?: 'newCard' | 'existingCard';
  newCard?: { sid: string };
  existingCard?: { token: string };
  error?: string;
};

export interface ProviderComponentProps {
  sid: string;
  payloadBase64: string;
}

// Coupon types (backend: POST /api/v1/scan2pay/coupons/apply)
export interface ApplyCouponRequest {
  validationNumber: string; // required
  ticketId: number;         // required, int32
}

export interface ApplyCouponResponse {
  discountId?: number; // int32
}

// Calculate Rate types (backend: POST /api/v1/scan2pay/tickets/calculate-rate)
export interface CalculateRateRequest {
  rate: string;           // required
  entryDate: string;      // ISO date-time, required
  exitDate: string;       // ISO date-time, required
}

export interface CalculateRateResponse {
  price: number | null;
}

// Contact Us types (backend: POST /api/v1/utils/contact-us)
export interface ContactUsContact {
  name?: string | null;
  email: string;      // required, max 254 chars
  phone?: string | null;
}

export interface ContactUsPayload {
  reason?: string | null;
  subject?: string | null;
  message: string;    // required, max 2048 chars
}

export interface ContactUsRequest {
  contact: ContactUsContact;
  payload: ContactUsPayload;
}

export interface ContactUsResponse {
  ticketId: number;
  message?: string | null;
}

